// src/lib/rpc/admin-api.ts
// Admin RPC Facade v2 — drop-in совместимая замена с единообразной обработкой результатов.
// Сохраняет ALL legacy-адаптеры и экспорты, но внутренне использует новый фасад.

import { supabase } from '@/integrations/supabase/client';
import { isAdminDomain } from '@/lib/domains/admin';
import { ensureSupabaseClientConfig } from '@/lib/env/runtime-env';
import { toBffList } from '@/lib/utils/bff-format';
import { callAction, callList, callSingle } from './facade';
import type {
  ActionResult,
  AuditLogRow,
  CustomerRow,
  EmailQueueRow,
  ListResult,
  PayoutRow,
  SecurityEventRow,
  SingleResult,
  TransactionRow,
  WebhookRow
} from './types';

// Телеметрия (безопасная)
import { track as _track } from '@/lib/telemetry';

// ───────────────────────────────────────────────────────────────────────────────
// Вспомогательные: среда, телеметрия, редактирование PII-логов
// ───────────────────────────────────────────────────────────────────────────────
type TelemetryEvent =
  | { event: 'rpc_legacy_adapter_used'; kind: 'list' | 'single'; label: string; env: string }
  | { event: 'rpc_generic_denied'; function_name: string; env: string };

const ENV_LABEL =
  (import.meta as any)?.env?.VITE_ENV_LABEL ??
  (typeof process !== 'undefined' ? process.env.VITE_ENV_LABEL : undefined) ??
  'unknown';

const IS_DEV = !!((import.meta as any)?.env?.DEV);

const { url: SUPABASE_URL, anonKey: SUPABASE_ANON_KEY } = ensureSupabaseClientConfig();

// Нестрогий импорт track: не падаем, если телеметрия не инициализирована
const safeTrack = (e: TelemetryEvent) => {
  // Disable legacy adapter telemetry in staging/production
  if (e.event === 'rpc_legacy_adapter_used' && ENV_LABEL !== 'DEV') return;
  try {
    (_track as any)?.(e.event, e);
  } catch {
    /* no-op */
  }
};

// Антифлуд/анти-мемликовый учёт телеметрии
const seen = new Set<string>();
const lastSent = new Map<string, number>();

function trackOncePerMinute(e: TelemetryEvent, key: string) {
  const now = Date.now();
  const prev = lastSent.get(key) ?? 0;
  if (now - prev > 60_000) {
    safeTrack(e);
    lastSent.set(key, now);
  }
}

// HMR-safe очистка структур раз в 10 минут
declare global {
  interface Window {
    __rpcSeenPurge?: number;
  }
}
if (typeof window !== 'undefined' && !window.__rpcSeenPurge) {
  window.__rpcSeenPurge = window.setInterval(() => {
    seen.clear();
    lastSent.clear();
  }, 10 * 60 * 1000);
}

// HMR dispose hook to prevent timer leaks in dev
if ((import.meta as any)?.hot) {
  (import.meta as any).hot?.dispose(() => {
    if (typeof window !== 'undefined' && window.__rpcSeenPurge) {
      clearInterval(window.__rpcSeenPurge);
      window.__rpcSeenPurge = undefined as any;
    }
  });
}

function noteLegacyUse(kind: 'list' | 'single', label?: string) {
  const k = `${kind}:${label || 'unknown'}`;
  if (!seen.has(k)) {
    if (seen.size > 2000) seen.clear(); // предохранитель от утечек в длинных SPA-сессиях
    seen.add(k);
    if (IS_DEV && typeof window !== 'undefined') {
      // Без PII; только служебная метка
      console.warn(`[rpc:${ENV_LABEL}] legacy adapter used: ${k}`);
    }
  }
  trackOncePerMinute(
    { event: 'rpc_legacy_adapter_used', kind, label: label || 'unknown', env: ENV_LABEL },
    k,
  );
}

// Strict RPC-only: без рантайм-байпаса; контроль через ESLint/CI.
function assertLegacyAllowed() {
  // Legacy adapters removed in Security Gate - все через фасад
}

// ───────────────────────────────────────────────────────────────────────────────
// Единый legacy-shape (совместимость с текущими экранами)
// ───────────────────────────────────────────────────────────────────────────────
export type LegacyResult<T> = { data: T | null; error: any | null };

/** @deprecated Используйте каноничный { ok, data } | { ok: false, error } */
export async function toLegacyList<T>(
  p: Promise<ListResult<T>>,
  logLabel?: string,
): Promise<LegacyResult<T[]>> {
  assertLegacyAllowed();

  try {
    const r = await p;
    noteLegacyUse('list', logLabel);

    if (r.ok) {
      return { data: r.data, error: null };
    }

    // Type assertion for error case
    type ErrorVariant = { ok: false; error: unknown; disabled?: boolean; source?: unknown };
    const errorResult = r as ErrorVariant;
    const err = errorResult.error instanceof Error ? errorResult.error : new Error(String(errorResult.error));
    (err as any).source = errorResult.source;
    (err as any).disabled = errorResult.disabled;
    return { data: null, error: err };
  } catch (e) {
    noteLegacyUse('list', logLabel);
    const err = e instanceof Error ? e : new Error(String(e));
    (err as any).source = 'thrown';
    (err as any).disabled = false;
    return { data: null, error: err };
  }
}

/** @deprecated Используйте каноничный { ok, data } | { ok: false, error } */
export async function toLegacySingle<T = any>(
  p: Promise<SingleResult<T> | ActionResult>,
  logLabel?: string,
): Promise<LegacyResult<T>> {
  assertLegacyAllowed();

  try {
    const r = await p;
    noteLegacyUse('single', logLabel);

    if (r && r.ok) {
      return { data: (r.data as T) ?? null, error: null };
    }

    // Type assertion for error case
    type ErrorVariant = { ok: false; error: unknown; disabled?: boolean; source?: unknown };
    const errorResult = r as ErrorVariant;
    const err = errorResult?.error instanceof Error ? errorResult.error : new Error(String(errorResult?.error ?? 'rpc-failed'));
    (err as any).source = errorResult?.source;
    (err as any).disabled = errorResult?.disabled;
    return { data: null, error: err };
  } catch (e) {
    noteLegacyUse('single', logLabel);
    const err = e instanceof Error ? e : new Error(String(e));
    (err as any).source = 'thrown';
    (err as any).disabled = false;
    return { data: null, error: err };
  }
}

type EdgeCallConfig = {
  method?: 'GET' | 'POST';
  params?: Record<string, string | number | undefined | null>;
  body?: Record<string, unknown>;
  idempotencyKey?: string;
  logLabel?: string;
};

export async function callAdminEdgeLegacy<T = any>(
  functionName: string,
  cfg: EdgeCallConfig = {}
): Promise<LegacyResult<T>> {
  const { supabase } = await import('@/integrations/supabase/client');
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    return toLegacySingle<T>(Promise.resolve({ ok: false as const, error: new Error('missing-session'), source: 'action' as const }), cfg.logLabel);
  }

  // Build query parameters for Edge Function URL routing
  // Edge Function expects params like ?act=customers.list in URL, not as headers
  let functionUrl = functionName;
  if (cfg.params) {
    const queryParams = new URLSearchParams();
    Object.entries(cfg.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).length) {
        queryParams.append(key, String(value));
      }
    });
    const queryString = queryParams.toString();
    if (queryString) {
      functionUrl = `${functionName}?${queryString}`;
    }
  }

  // Build headers with auth only
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
  };

  if (SUPABASE_ANON_KEY) headers['apikey'] = SUPABASE_ANON_KEY;
  if (cfg.idempotencyKey) headers['Idempotency-Key'] = cfg.idempotencyKey;

  try {
    // Гейт: проверяем токен перед вызовом
    const { data: sess } = await supabase.auth.getSession();
    let token = sess.session?.access_token;

    if (!token) {
      // Мягкий рефреш если токен истёк
      await supabase.auth.refreshSession();
      const { data: s2 } = await supabase.auth.getSession();
      token = s2.session?.access_token;

      if (!token) {
        // Контролируемая ошибка без ретраев
        const actionResult = { ok: false as const, error: 'Authentication required', source: 'action' as const };
        return toLegacySingle<T>(Promise.resolve(actionResult), cfg.logLabel);
      }
    }

    // Get internal secret for Edge Function authentication
    const internalSecret = (import.meta as any)?.env?.VITE_INTERNAL_EDGE_SECRET
      || (typeof process !== 'undefined' ? process.env.VITE_INTERNAL_EDGE_SECRET : undefined);

    // Direct fetch to Edge Function with query parameters
    // Supabase SDK doesn't support query params in functions.invoke, so we use direct fetch
    const functionBaseUrl = `${SUPABASE_URL}/functions/v1/${functionUrl}`;

    const response = await fetch(functionBaseUrl, {
      method: cfg.method || 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(internalSecret ? { 'x-internal-secret': internalSecret } : {})
      },
      body: cfg.body ? JSON.stringify(cfg.body) : undefined
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Function invocation failed: ${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error?.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      const actionResult = { ok: false as const, error: errorMessage, source: 'action' as const };
      return toLegacySingle<T>(Promise.resolve(actionResult), cfg.logLabel);
    }

    const data = await response.json();
    const actionResult = { ok: true as const, data: data as T, source: 'action' as const };
    return toLegacySingle<T>(Promise.resolve(actionResult), cfg.logLabel);
  } catch (err) {
    const actionResult = { ok: false as const, error: err instanceof Error ? err.message : 'Unknown error', source: 'action' as const };
    return toLegacySingle<T>(Promise.resolve(actionResult), cfg.logLabel);
  }
}

// ───────────────────────────────────────────────────────────────────────────────
// Админ-домены (обёртки над RPC). Все методы возвращают строгий Result, но
// для обратной совместимости используют toLegacy* там, где это уже нужно.
// ───────────────────────────────────────────────────────────────────────────────

/** Admin – Webhooks */
export const AdminWebhooks = {
  recent: (limit = 25) =>
    toLegacyList<WebhookRow>(callList<WebhookRow>('webhooks.recent', { limit }, { logLabel: 'webhooks.recent' }), 'webhooks.recent'),
  securitySummary: () =>
    toLegacySingle<any>(callSingle<any>('webhooks.security.summary', {}, { logLabel: 'webhooks.security-summary' }), 'webhooks.security-summary'),
  statsSince: (sinceISO: string) =>
    toLegacySingle<{ total: number; invalid_last_24h: number }>(
      callSingle('admin_webhooks_stats', { _since: sinceISO }, { logLabel: 'webhooks.stats-since' }), 'webhooks.stats-since'
    ),
  webhooksStats: () =>
    toLegacySingle<any>(callSingle<any>('admin_webhooks_stats', {}, { logLabel: 'webhooks.stats' }), 'webhooks.stats'),
};

/** Admin – Customers */
export const AdminCustomers = {
  /**
   * List customers with safe fields only.
   * @param q search string
   * @param limit page size
   * @param offset page offset (default 0 for backward compatibility)
   * @returns BFF formatted response with pagination info
   */
  listSafe: async (q: string, limit = 5, offset = 0) => {
    // Use customers.list edge handler with query parameters
    const result = await callAdminEdgeLegacy<any>('admin', {
      method: 'POST',
      params: {
        act: 'customers.list',
        _search: q,
        _limit: limit.toString(),
        _offset: offset.toString()
      }
    });

    if (result.error) {
      return { data: null, error: result.error };
    }

    // Edge Function returns {data: Customer[], total, limit, offset}
    const responseData = result.data;
    if (!responseData || typeof responseData !== 'object') {
      return { data: toBffList([], 0, limit, offset), error: null };
    }

    // Extract data array and total from response
    const customers = Array.isArray(responseData.data) ? responseData.data : [];
    const total = typeof responseData.total === 'number' ? responseData.total : 0;

    return {
      data: toBffList(customers, total, limit, offset),
      error: null
    };
  },

  getUnified: (customerId?: string) =>
    customerId
      ? toLegacySingle<any>(
        callSingle<any>('admin_get_unified_customers', { _customer_id: customerId }, { logLabel: 'customers.get-unified' }),
        'customers.get-unified'
      )
      : toLegacyList<any>(
        callList<any>('admin_get_unified_customers', {}, { logLabel: 'customers.get-unified' }),
        'customers.get-unified'
      ),

  overview: async (customerId: string) => {
    // Direct RPC call to admin_customer_overview for reliability
    // Bypass Edge Function to avoid CORS/network issues
    const result = await callSingle<any>(
      'admin_customer_overview',
      { customer_id: customerId },
      { logLabel: 'customers.overview' }
    );

    if (result.error) {
      console.error('[AdminCustomers.overview] RPC failed:', result.error);
      return { data: null, error: result.error };
    }

    // 🛡️ CRITICAL FIX: If RPC returns empty object or null, try fallback to Edge Function
    if (!result.data || (typeof result.data === 'object' && Object.keys(result.data).length === 0)) {
      console.warn('[AdminCustomers.overview] RPC returned empty data, trying Edge Function fallback');
      try {
        const edgeResult = await callAdminEdgeLegacy<any>('admin', {
          method: 'POST',
          params: {
            act: 'customers.list',
            _search: customerId,
            _limit: '1',
            _offset: '0'
          }
        });

        if (edgeResult.data?.data?.[0]) {
          return { data: edgeResult.data.data[0], error: null };
        }
      } catch (fallbackError) {
        console.error('[AdminCustomers.overview] Edge Function fallback failed:', fallbackError);
      }
    }

    return { data: result.data, error: null };
  },
  listTransactionsV1: (customerId: string, limit = 100, offset = 0) =>
    toLegacyList<TransactionRow>(
      callList<TransactionRow>(
        'admin_list_customer_transactions',
        { p_customer_id: customerId, p_limit: limit, p_offset: offset },
        { logLabel: 'customers.transactions' },
      ),
      'customers.transactions',
    ),

  listLiquidationAddresses: (
    customerId: string | null,
    chain: string | null,
    currency: string | null,
    status: string | null,
    limit = 50,
    offset = 0
  ) =>
    toLegacySingle<any>(
      callSingle<any>(
        'admin_list_liquidation_addresses',
        {
          customer_id_param: customerId,
          chain_param: chain,
          currency_param: currency,
          status_param: status,
          limit_param: limit,
          offset_param: offset
        },
        { logLabel: 'customers.liquidation-addresses' }
      ),
      'customers.liquidation-addresses'
    ),

  listVirtualAccounts: (
    customerId?: string | null,
    status?: string | null,
    currency?: string | null,
    limit = 50,
    offset = 0
  ) =>
    toLegacySingle<any>(
      callSingle<any>(
        'admin_list_virtual_accounts',
        {
          customer_id_param: customerId ?? null,
          status_param: status ?? null,
          currency_param: currency ?? null,
          limit_param: limit,
          offset_param: offset
        },
        { logLabel: 'customers.virtual-accounts' }
      ),
      'customers.virtual-accounts'
    ),

  listTransfers: (limit = 50, offset = 0, status = 'all') =>
    toLegacySingle<any>(
      callSingle<any>(
        'admin_list_transfers',
        { p_limit: limit, p_offset: offset, status },
        { logLabel: 'customers.transfers' }
      ),
      'customers.transfers'
    ),

  getByIdentifier: (identifier: string) =>
    toLegacySingle(
      callSingle('get_customer_by_identifier', { identifier }, { logLabel: 'customers.get-by-identifier' }),
      'customers.get-by-identifier'
    ),
};

/** Admin – System Advanced */
export const AdminSystemAdvanced = {
  getAdminRateLimitCooldown: (adminUserId: string) =>
    toLegacySingle<any>(callSingle<any>('get_admin_rate_limit_cooldown', { admin_user_id: adminUserId }, { logLabel: 'system.rate-limit-cooldown' }), 'system.rate-limit-cooldown'),
  testEncryptionSystem: () =>
    toLegacySingle<any>(callSingle<any>('test_encryption_system', {}, { logLabel: 'system.test-encryption' }), 'system.test-encryption'),
  recomputeCustomerCapabilities: (customerId: string) =>
    toLegacySingle<any>(callAction('recompute_customer_capabilities', { customer_id: customerId }, { logLabel: 'system.recompute-capabilities' }), 'system.recompute-capabilities'),
};

/** Admin – Activity & Monitoring */
export const AdminActivity = {
  recentTransactions: (limit = 25) =>
    toLegacyList<TransactionRow>(
      callList<TransactionRow>('admin_recent_transactions', { limit_count: limit }, { logLabel: 'activity.recent-transactions' }),
      'activity.recent-transactions',
    ),
  recentPayouts: (limit = 25) =>
    toLegacyList<PayoutRow>(callList<PayoutRow>('admin_recent_payouts', { limit_count: limit }, { logLabel: 'activity.recent-payouts' }), 'activity.recent-payouts'),
  activityMetrics: (window: string) =>
    toLegacySingle<any>(
      callSingle<any>('admin_activity_metrics', {}, { logLabel: `activity.metrics.${window}` }),
      'activity.metrics',
    ),
  pendingAlerts: (limit = 50) =>
    toLegacyList<any>(callList<any>('admin_pending_operations_alerts', { limit_count: limit }, { logLabel: 'activity.pending-alerts' }), 'activity.pending-alerts'),
  recentEmailQueue: (limit = 25) =>
    toLegacyList<EmailQueueRow>(callList<EmailQueueRow>('admin_recent_email_queue', { limit_count: limit }, { logLabel: 'activity.recent-email-queue' }), 'activity.recent-email-queue'),
  recentAuditLogs: (limit = 10) =>
    toLegacyList<AuditLogRow>(callList<AuditLogRow>('admin_recent_audit_logs', { p_limit: limit }, { logLabel: 'activity.recent-audit-logs' }), 'activity.recent-audit-logs'),
  dailyHealthSnapshot: () =>
    toLegacySingle<any>(callSingle<any>('daily_admin_health_snapshot', {}, { logLabel: 'activity.daily-health-snapshot' }), 'activity.daily-health-snapshot'),
};

/** Admin – Logging & Events */
export const AdminLogging = {
  securityEventsRecent: (limitRows = 50) =>
    toLegacyList<SecurityEventRow>(callList<SecurityEventRow>('admin_security_events_recent', { limit_rows: limitRows }, { logLabel: 'logging.security-events-recent' }), 'logging.security-events-recent'),
  qaRunsRecent: (limitRows = 5) =>
    toLegacyList<any>(callList<any>('admin_qa_runs_recent', { limit_rows: limitRows }, { logLabel: 'logging.qa-runs-recent' }), 'logging.qa-runs-recent'),
};

/** Admin – Email */
export const AdminEmail = {
  getEmailQueue: (limit = 50) =>
    toLegacyList(callList('admin_list_email_queue', { _limit: limit, _offset: 0 }, { logLabel: 'email.list-queue' }), 'email.list-queue'),
  getEmailMetrics: (days = 30) =>
    toLegacySingle<any>(callSingle<any>('admin_get_email_metrics', { _days: days }, { logLabel: 'email.get-metrics' }), 'email.get-metrics'),
  retryFailedEmails: (emailIds?: string[]) =>
    toLegacySingle<any>(callSingle<any>('admin_retry_failed_emails', { email_ids: emailIds }, { logLabel: 'email.retry-failed' }), 'email.retry-failed'),
  getQueueStats: () =>
    toLegacySingle<any>(callSingle<any>('admin_get_email_queue_stats', {}, { logLabel: 'email.queue-stats' }), 'email.queue-stats'),
  queueHealth: () =>
    toLegacySingle<any>(callSingle<any>('admin_email_queue_health', {}, { logLabel: 'email.queue-health' }), 'email.queue-health'),
  cancel: (emailId: string) =>
    toLegacySingle<any>(callAction('admin_cancel_email', { email_id: emailId }, { logLabel: 'email.cancel' }), 'email.cancel'),
  resetStuckJobs: (timeoutHours = 1) =>
    toLegacySingle<any>(callAction('admin_reset_stuck_email_jobs', { timeout_hours: timeoutHours }, { logLabel: 'email.reset-stuck-jobs' }), 'email.reset-stuck-jobs'),
};

/** Admin – Security (P3 Bridge Only) */
export const AdminSecurity = {
  // Direct RPC approach - bypasses edge function to avoid recursion
  isAdminSimple: async (): Promise<LegacyResult<boolean>> => {
    try {
      // E2E Testing Bypass
      if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_admin_bypass') === 'true') {
        return { data: true, error: null };
      }

      const { supabase } = await import('@/integrations/supabase/client');

      // Get current user for explicit user_id parameter
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: false, error: new Error('User not authenticated') };
      }

      // Direct RPC call with timeout control
      // Function signature: is_admin_simple(user_id UUID DEFAULT NULL)
      // Pass empty object to use DEFAULT (auth.uid()), or explicitly pass user_id
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 seconds timeout

      try {
        // Pass function params as first arg, signal in options (third arg)
        const { data, error } = await supabase.rpc('is_admin_simple', {}, {
          signal: controller.signal
        } as any) as { data: boolean | null; error: any };
        clearTimeout(timeoutId);

        if (error) {
          console.error('[AdminSecurity.isAdminSimple] RPC error:', error);
          const err = error instanceof Error
            ? error
            : new Error(String(error?.message || 'rpc_failed'));
          (err as any).code = error?.code || 'rpc_error';
          return { data: false, error: err };
        }

        return { data: Boolean(data), error: null };
      } catch (rpcErr) {
        clearTimeout(timeoutId);
        console.error('[AdminSecurity.isAdminSimple] RPC exception:', rpcErr);
        const err = rpcErr instanceof Error ? rpcErr : new Error(String(rpcErr));
        (err as any).code = 'rpc_exception';
        return { data: false, error: err };
      }
    } catch (e) {
      console.error('[AdminSecurity.isAdminSimple] Exception:', e);
      const err = e instanceof Error ? e : new Error(String(e));
      return { data: false, error: err };
    }
  },

  // Minimal compatibility functions
  raiseOpsAlert: (severity: string, message: string, metadata: any) =>
    toLegacySingle<any>(callAction('raise_ops_alert', { severity, message, metadata }, { logLabel: 'security.raise-ops-alert' }), 'security.raise-ops-alert'),
  logSecurityEvent: (eventType: string, metadata: any, userId?: string) => {
    if (typeof window !== 'undefined' && !isAdminDomain()) {
      return Promise.resolve({ data: null, error: null });
    }
    return toLegacySingle<any>(
      callAction('log_security_event', { event_type: eventType, metadata, user_id: userId }, { logLabel: 'security.log-security-event' }),
      'security.log-security-event'
    );
  },
  recentAuditLogs: (limit = 100) =>
    toLegacyList<AuditLogRow>(callList<AuditLogRow>('admin_recent_audit_logs', { p_limit: limit }, { logLabel: 'security.recent-audit-logs' }), 'security.recent-audit-logs'),
};

/** Admin – System */
export const AdminSystem = {
  cleanupInvalidWebhooks: () =>
    toLegacySingle<{ cleaned: number }>(callSingle<{ cleaned: number }>('admin_cleanup_invalid_webhooks', {}, { logLabel: 'system.cleanup-invalid-webhooks' }), 'system.cleanup-invalid-webhooks'),
  hideOldAwaitingTransactions: () =>
    toLegacySingle<any>(callAction('admin_hide_old_awaiting_transactions', {}, { logLabel: 'system.hide-old-awaiting-transactions' }), 'system.hide-old-awaiting-transactions'),
  summary: () =>
    toLegacySingle<any>(callSingle<any>('admin_summary', {}, { logLabel: 'system.summary' }), 'system.summary'),
  peopleCount: () =>
    toLegacySingle<any>(callSingle<any>('admin_people_count', {}, { logLabel: 'system.people-count' }), 'system.people-count'),
  cleanupEvents: (eventType: string, days: number) =>
    toLegacySingle<any>(callAction('admin_cleanup_events', { _event_type: eventType, _days: days }, { logLabel: 'system.cleanup-events' }), 'system.cleanup-events'),
  getSystemHealthMetrics: () =>
    toLegacySingle<any>(callSingle<any>('get_system_health_metrics', {}, { logLabel: 'system.get-system-health-metrics' }), 'system.get-system-health-metrics'),
  getAdminRateLimitCooldown: (adminUserId: string) =>
    toLegacySingle<any>(callSingle<any>('get_admin_rate_limit_cooldown', { admin_user_id: adminUserId }, { logLabel: 'system.get-admin-rate-limit-cooldown' }), 'system.get-admin-rate-limit-cooldown'),
  ensureBridgeIdentity: (userId: string) =>
    toLegacySingle<any>(callAction('ensure_bridge_identity', { user_id: userId }, { logLabel: 'system.ensure-bridge-identity' }), 'system.ensure-bridge-identity'),
  recomputeCustomerCapabilities: (userId: string) =>
    toLegacySingle<any>(callAction('recompute_customer_capabilities', { user_id: userId }, { logLabel: 'system.recompute-customer-capabilities' }), 'system.recompute-customer-capabilities'),

  // System health metrics (новая имплементация)
  systemHealthMetrics: () =>
    toLegacySingle<any>(callSingle<any>('get_system_health_metrics', {}, { logLabel: 'system.health-metrics' }), 'system.health-metrics'),

  // Ручной прогон синка (пример action-вызова)
  triggerManualSync: (service_name: string, parameters?: Record<string, any>) =>
    toLegacySingle<any>(callAction('admin_trigger_manual_sync', { service_name, parameters }, { logLabel: 'system.trigger-manual-sync' }), 'system.trigger-manual-sync'),
};

/** Admin – Data Quality */
export const AdminDataQuality = {
  getUnifiedDataQualityStats: () =>
    toLegacySingle<any>(callSingle<any>('get_unified_data_quality_stats', {}, { logLabel: 'data-quality.get-unified-data-quality-stats' }), 'data-quality.get-unified-data-quality-stats'),
  fixOrphanedTransactionsUnified: () =>
    toLegacySingle<any>(callAction('fix_orphaned_transactions_unified', {}, { logLabel: 'data-quality.fix-orphaned-transactions-unified' }), 'data-quality.fix-orphaned-transactions-unified'),
  fixOrphanedPayoutsUnified: () =>
    toLegacySingle<any>(callAction('fix_orphaned_payouts_unified', {}, { logLabel: 'data-quality.fix-orphaned-payouts-unified' }), 'data-quality.fix-orphaned-payouts-unified'),
  getBankParityGaps: () =>
    toLegacySingle<any>(callSingle<any>('admin_get_bank_parity_gaps', {}, { logLabel: 'data-quality.get-bank-parity-gaps' }), 'data-quality.get-bank-parity-gaps'),

  getDataIntegrityAudit: () =>
    toLegacySingle(callSingle('get_data_integrity_audit', {}, { logLabel: 'dq.get-audit' }), 'dq.get-audit'),
  prepareRlsTransition: () =>
    toLegacySingle(callAction('prepare_rls_transition', {}, { logLabel: 'dq.prepare-rls' }), 'dq.prepare-rls'),
  runDataQualityMaintenance: () =>
    toLegacySingle(callAction('run_data_quality_maintenance', {}, { logLabel: 'dq.maintenance' }), 'dq.maintenance'),
};

/** Admin – Operations */
export const AdminOperations = {
  pendingOperationsAlerts: (limit: number) =>
    toLegacyList<any>(callList<any>('admin_pending_operations_alerts', { limit_count: limit }, { logLabel: 'operations.pending-operations-alerts' }), 'operations.pending-operations-alerts'),
  raiseOpsAlert: (severity: string, message: string, metadata: any) =>
    toLegacySingle<any>(callAction('raise_ops_alert', { severity, message, metadata }, { logLabel: 'operations.raise-ops-alert' }), 'operations.raise-ops-alert'),
  callGeneric: (functionName: string) => {
    const ALLOWED = ['admin_onboarding_summary', 'admin_summary', 'admin_people_count'] as const;
    type AllowedGeneric = typeof ALLOWED[number];

    if (!ALLOWED.includes(functionName as AllowedGeneric)) {
      safeTrack({
        event: 'rpc_generic_denied',
        function_name: functionName,
        env: ENV_LABEL
      } satisfies TelemetryEvent);
      return Promise.resolve({ data: null, error: new Error('rpc-not-allowed') });
    }
    return toLegacySingle<any>(callSingle<any>(functionName as AllowedGeneric, {}, { logLabel: `operations.call-generic.${functionName}` }), `operations.call-generic.${functionName}`);
  },
  onboardingSummary: () =>
    toLegacySingle<any>(callSingle<any>('admin_onboarding_summary', {}, { logLabel: 'operations.onboarding-summary' }), 'operations.onboarding-summary'),
  recentOnboardingEvents: (limit = 10) =>
    toLegacyList<any>(callList<any>('admin_recent_onboarding_events', { _limit: limit }, { logLabel: 'operations.recent-onboarding-events' }), 'operations.recent-onboarding-events'),
};

/** Admin – QA */
export const AdminQA = {
  qaSummary: () =>
    toLegacySingle<any>(callSingle<any>('admin_qa_summary', {}, { logLabel: 'qa.qa-summary' }), 'qa.qa-summary'),
  qaFailingTests: () =>
    toLegacyList<any>(callList<any>('admin_qa_failing_tests', {}, { logLabel: 'qa.qa-failing-tests' }), 'qa.qa-failing-tests'),
  qaAuditFindings: () =>
    toLegacyList<any>(callList<any>('admin_qa_audit_findings', {}, { logLabel: 'qa.qa-audit-findings' }), 'qa.qa-audit-findings'),
  qaFixRunsList: (limitRows = 50) =>
    toLegacyList<any>(callList<any>('admin_qa_fix_runs_list', { limit_rows: limitRows }, { logLabel: 'qa.qa-fix-runs-list' }), 'qa.qa-fix-runs-list'),
  qaRunsRecent: (limitRows = 5) =>
    toLegacyList<any>(callList<any>('admin_qa_runs_recent', { limit_rows: limitRows }, { logLabel: 'qa.qa-runs-recent' }), 'qa.qa-runs-recent'),
  securityEventsRecent: (limitRows = 50) =>
    toLegacyList<SecurityEventRow>(callList<SecurityEventRow>('admin_security_events_recent', { limit_rows: limitRows }, { logLabel: 'qa.security-events-recent' }), 'qa.security-events-recent'),
};

/** Admin – Notifications */
export const AdminNotifications = {
  getNotificationPreferences: async (customerId: string) => {
    // Use wallet function to get notification preferences
    // Wallet function requires action in query string for GET or in body for POST
    try {
      const sessionResult = await supabase.auth.getSession();
      const session = sessionResult?.data?.session;

      if (!session?.access_token) {
        return { data: null, error: new Error('No active session') };
      }

      // Use query string for GET request (body may not be sent with GET)
      const { data, error } = await supabase.functions.invoke('wallet?action=user.notificationPreferences', {
        method: 'GET'
      });

      if (error) {
        console.error('[AdminNotifications] Error getting preferences:', error);
        return { data: null, error };
      }

      // Return as array for compatibility with existing code
      return { data: data ? [data] : [], error: null };
    } catch (err) {
      console.error('[AdminNotifications] Exception getting preferences:', err);
      return { data: null, error: err instanceof Error ? err : new Error('Failed to get notification preferences') };
    }
  },
  updateNotificationPreferences: async (customerId: string, preferences: any) => {
    // Use wallet function to update notification preferences
    try {
      const sessionResult = await supabase.auth.getSession();
      const session = sessionResult?.data?.session;

      if (!session?.access_token) {
        return { data: null, error: new Error('No active session') };
      }

      // POST request with action in body
      const { data, error } = await supabase.functions.invoke('wallet', {
        method: 'POST',
        body: {
          action: 'user.notificationPreferences',
          ...preferences
        }
      });

      if (error) {
        console.error('[AdminNotifications] Error updating preferences:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('[AdminNotifications] Exception updating preferences:', err);
      return { data: null, error: err instanceof Error ? err : new Error('Failed to update notification preferences') };
    }
  },
};

/** Admin – Unified Customers */
export const AdminUnifiedCustomers = {
  getUnifiedCustomers: async () => {
    const result = await toLegacyList<CustomerRow>(
      callList<CustomerRow>('admin_get_unified_customers', {}, { logLabel: 'unified-customers.get-unified-customers' }),
      'unified-customers.get-unified-customers'
    );

    // Defensive normalization: ensure data is always an array
    if (result.error) {
      return { data: [], error: result.error };
    }

    const customers = Array.isArray(result.data) ? result.data : [];
    // Ensure each customer object has required fields to prevent Object.keys() crashes
    const normalizedCustomers = customers.map(customer => ({
      ...customer, // spread original data first
      // Apply defaults only for missing fields
      email: customer?.email || 'unknown',
      display_name: customer?.display_name || 'N/A',
      type: customer?.type || 'individual',
      kyc_status: customer?.kyc_status || 'pending'
    }));

    return { data: normalizedCustomers, error: null };
  },

  ensureBridgeIdentity: (customerId: string) =>
    toLegacySingle<any>(callAction('ensure_bridge_identity', { customer_id: customerId }, { logLabel: 'unified-customers.ensure-bridge-identity' }), 'unified-customers.ensure-bridge-identity'),
};

/** Admin – Webhook Security */
export const AdminWebhookSecurity = {
  getWebhookSecuritySummary: (includeInvalid = false) =>
    toLegacySingle<any>(callSingle<any>('get_webhook_security_summary', { include_invalid: includeInvalid }, { logLabel: 'webhook-security.get-webhook-security-summary' }), 'webhook-security.get-webhook-security-summary'),
};

/** User-facing RPCs (non-admin) */
export const UserApi = {
  // Bank Accounts
  // Graceful handling: return empty result if customerId is not provided
  getUserBankAccounts: (customerId: string) => {
    if (!customerId || typeof customerId !== 'string' || customerId.trim() === '') {
      console.warn('[UserApi.getUserBankAccounts] No customerId provided, returning empty result');
      return Promise.resolve({ data: [], error: null });
    }
    return toLegacyList(callList('get_user_bank_accounts', { target_customer_id: customerId, row_limit: 25 }, { logLabel: 'user.get-user-bank-accounts' }), 'user.get-user-bank-accounts');
  },

  // Profile / Identities
  getSelfIdentities: () =>
    toLegacyList(callList('get_self_identities', {}, { logLabel: 'user.get-self-identities' }), 'user.get-self-identities'),
  getSelfProfileSummary: () =>
    toLegacySingle(callSingle('get_self_profile_summary', {}, { logLabel: 'user.get-self-profile-summary' }), 'user.get-self-profile-summary'),

  // Onboarding
  onboardingBootstrap: () =>
    toLegacySingle(callSingle('onboarding_user', {}, { logLabel: 'user.onboarding-user' }), 'user.onboarding-user'),
  onboardingStatus: (params: Record<string, any>) =>
    toLegacySingle(callSingle('onboarding_status', params, { logLabel: 'user.onboarding-status' }), 'user.onboarding-status'),

  // Auth
  authPreflight: (email: string) =>
    toLegacySingle(callSingle('auth_preflight', { _email: email }, { logLabel: 'user.auth-preflight' }), 'user.auth-preflight'),

  // Payout Routes - RPC-only compliance
  listPayoutRoutes: (limit = 100) =>
    toLegacyList<any>(callList<any>('get_self_payout_routes', { _limit: limit }, { logLabel: 'user.list-payout-routes' }), 'user.list-payout-routes'),
  setDefaultPayoutRoute: (routeId: string) =>
    toLegacySingle<boolean>(callSingle<boolean>('set_self_default_payout_route', { _route_id: routeId }, { logLabel: 'user.set-default-payout-route' }), 'user.set-default-payout-route'),
  deletePayoutRoute: (routeId: string) =>
    toLegacySingle<boolean>(callSingle<boolean>('delete_self_payout_route', { _route_id: routeId }, { logLabel: 'user.delete-payout-route' }), 'user.delete-payout-route'),

  // Customer & Auth
  getCustomerIdForAuth: (authUid?: string) =>
    toLegacySingle<string>(callSingle<string>('get_customer_id_for_auth', { _auth_uid: authUid ?? null }, { logLabel: 'user.get-customer-id-for-auth' }), 'user.get-customer-id-for-auth'),

  // Security & Roles
  hasRole: (role: string, userId?: string) =>
    toLegacySingle<boolean>(callSingle<boolean>('has_role', { _role: role, _user_id: userId ?? null }, { logLabel: 'user.has-role' }), 'user.has-role'),
  logSecurityEvent: (eventType: string, metadata: any, userId?: string) =>
    toLegacySingle<any>(callAction('log_security_event', { event_type: eventType, metadata, user_id: userId ?? null }, { logLabel: 'user.log-security-event' }), 'user.log-security-event'),
};

/** Admin – Bridge & Verification */
export const AdminBridge = {};

/** Admin – Email Operations */
export const AdminEmailOps = {
  queueHealth: () =>
    toLegacySingle<any>(callSingle<any>('admin_email_queue_health', {}, { logLabel: 'email-ops.queue-health' }), 'email-ops.queue-health'),
  resetStuckEmailJobs: (maxMinutes: number) =>
    toLegacySingle<any>(callAction('admin_reset_stuck_email_jobs', { max_minutes: maxMinutes }, { logLabel: 'email-ops.reset-stuck-email-jobs' }), 'email-ops.reset-stuck-email-jobs'),
  recentEmailQueue: (limit = 25) =>
    toLegacyList<EmailQueueRow>(callList<EmailQueueRow>('admin_recent_email_queue', { limit_count: limit }, { logLabel: 'email-ops.recent-email-queue' }), 'email-ops.recent-email-queue'),
  enqueueEmail: (templateName: string, recipientEmail: string, data: any) =>
    toLegacySingle<any>(callAction('admin_enqueue_email', { template_name: templateName, recipient_email: recipientEmail, template_data: data }, { logLabel: 'email-ops.enqueue-email' }), 'email-ops.enqueue-email'),
  retryEmail: (emailId: string) =>
    toLegacySingle<any>(callAction('admin_retry_email', { email_id: emailId }, { logLabel: 'email-ops.retry-email' }), 'email-ops.retry-email'),
  cancelEmail: (emailId: string) =>
    toLegacySingle<any>(callAction('admin_cancel_email', { email_id: emailId }, { logLabel: 'email-ops.cancel-email' }), 'email-ops.cancel-email'),
};

/** Edge Functions & Special Operations */
export const EdgeApi = {
  // Edge function calls - allowed outside RPC facade
  businessIntelligence: (reportType: string) =>
    fetch('/api/business-intelligence', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reportType })
    }),
};


// Aliases to remove "Legacy" terminology - migration path
// Aliases to remove "Legacy" terminology - migration path
export const toResultList = toLegacyList;
export const toResultSingle = toLegacySingle;

// Default export for backward compatibility
const AdminAPI = {
  // Legacy adapters
  toLegacyList,
  toLegacySingle,
  // New aliases
  toResultList: toLegacyList,
  toResultSingle: toLegacySingle,
  // Domain modules
  AdminWebhooks,
  AdminCustomers,
  AdminSystemAdvanced,
  AdminActivity,
  AdminLogging,
  AdminEmail,
  AdminSecurity,
  AdminSystem,
  AdminDataQuality,
  AdminOperations,
  AdminQA,
  AdminNotifications,
  AdminUnifiedCustomers,
  AdminWebhookSecurity,
  UserApi,
  AdminBridge,
  AdminEmailOps,
  EdgeApi,
};

export default AdminAPI;